import type { TDocumentDefinitions, Column, Content, TableCell } from "pdfmake/interfaces";
import moment from "moment";
import { Aircraft, getWbMaxMass } from "../models/aircraft";
import { getCrewFullName } from "../models/crew";
import Flight, { FlightFuelOnBoard, FlightWbLoad } from "../models/flight";
import { FlightPlanPoint } from "../models/flightPlan";
import { CalculatedFlightWbPoints, checkPointOutOfEnvelope, hasLatEnvelopePoints } from "../models/flightWb";
import { FuelConverter } from "./FuelUtils";

/**
 * Shared Weight & Balance loadsheet logic.
 *
 * ONE source of truth drives BOTH the on-screen sheet (FlightWbPage, FlyWbRevisionPage) and the A4
 * PDF that is merged into the briefing pack: buildWbSheetRows/buildWbLimitRows assemble the data,
 * renderWbEnvelopeSvg draws the CG chart as a vector SVG string (inlined on screen and embedded in
 * the PDF via pdfmake's svg node), and buildWbLoadsheetDocDefinition lays out one A4 page per leg
 * (two when a very long table leaves no room for the charts). This replaces the old html2canvas
 * screenshot pipeline.
 */

// palette (kept in sync with css/wb-loadsheet.css)
// Mono brand chrome (BRIEF's identity is black-on-white) + one validated color per flight
// phase — the ONLY saturated marks on the sheet. Trio machine-checked for CVD (worst pair
// ΔE 9.7 deutan) with ◆●◇ shape-coding as secondary encoding.
const INK = "#0d131c", INK2 = "#333c46", MUT = "#6a7885", FAINT = "#96a2ad", LINE = "#d7dee6",
      SEC_BG = "#F7F7F7", ENV = "#4b5563", TRAVEL = "#94a3b8",
      TOW_C = "#1e5fae", TOW_BG = "#edf2f8",
      LDG_C = "#c2610c", LDG_BG = "#fbf4ee",
      ZFW_C = "#7c3aed", ZFW_BG = "#f6f1fd",
      OK = "#137a52", OKBG = "#e7f4ee",
      BAD = "#b3261e", BADBG = "#fbeceb",
      REV = "#8a5a00", REVBG = "#fdf6e3";

export type WbLoadsheetLeg = {
    index: number //0-based
    count: number
    from: string
    to: string
    eetMins: number
}

export type WbLoadsheetRevision = {
    n: number
    summary: string[] //one line per change, already formatted
    fuelChanged: boolean
}

export type WbLoadsheetInput = {
    flight: Flight
    wbLoads: FlightWbLoad[]
    fuelOnBoard: FlightFuelOnBoard[] //departure fuel of this sheet's leg (flight.fuelOnBoard for a one-leg flight)
    calc: CalculatedFlightWbPoints
    tripFuelMass: number //in aircraft massUnit, rounded
    errors: string[]
    warnings: string[]
    isLatArmInWbPointUp: boolean
    leg?: WbLoadsheetLeg //only for flights with intermediate stops
    revision?: WbLoadsheetRevision //last-minute revision addendum
    logoSvg?: string //brief_logo.svg content for the PDF header; falls back to a text wordmark
}

export type WbSheetRow = {
    kind: "bew" | "seat" | "bag" | "zfw" | "fuel" | "tow" | "trip" | "ldg"
    stationId?: string
    label: string
    name?: string
    personId?: string
    mass: number
    arm?: number
    latArm?: number
    //exact moments (mass × arm, kg·mm) — totals carry the calculation's sums so the moment
    //column always adds up, whatever the display rounding of the arms
    moment?: number
    latMoment?: number
    maxMass?: number //baggage stations, for the editor
}

export type WbLimitRow =
    | { type: "section", label: string }
    | { type: "limit", label: string, value: string, note: string, ok: boolean }

export function hasLatEnvelope(aircraft: Aircraft): boolean {
    return hasLatEnvelopePoints(aircraft)
}

export function wbRouteTitle(flight: Flight, leg?: WbLoadsheetLeg): string {
    if (leg) {
        return leg.from + " - " + leg.to
    }
    const pointName = (p: FlightPlanPoint) => p.isIcao ? p.icaoCode : p.name
    if (flight.route) {
        return flight.route.departure.name + " - " + flight.route.destination.name
    }
    if (flight.customRoute) {
        return pointName(flight.customRoute.departure) + " - " + pointName(flight.customRoute.destination)
    }
    return ""
}

// ─── rows ────────────────────────────────────────────────────────────────────

export function buildWbSheetRows(input: WbLoadsheetInput): WbSheetRow[] {
    const { wbLoads, fuelOnBoard, calc, tripFuelMass } = input
    const aircraft = input.flight.aircraft
    const wb = aircraft.wb!
    const withMoments = (r: WbSheetRow): WbSheetRow =>
        r.arm === undefined ? r : { ...r, moment: r.moment ?? r.mass * r.arm, latMoment: r.latMoment ?? r.mass * (r.latArm ?? 0) }

    const rows: WbSheetRow[] = []
    rows.push({ kind: "bew", label: "Empty (BEW)", mass: wb.emptyMass.mass, arm: wb.emptyMass.arm, latArm: wb.emptyMass.latArm })

    wb.stations.filter(s => s.type === "seat").forEach(s => {
        const load = wbLoads.find(l => l.station.id === s.id)
        rows.push({ kind: "seat", stationId: s.id, label: s.name, name: load?.name ?? "", personId: load?.personId, mass: load?.mass ?? 0, arm: s.arm, latArm: s.latArm })
    })

    wb.stations.filter(s => s.type === "baggage").forEach(s => {
        const load = wbLoads.find(l => l.station.id === s.id)
        rows.push({ kind: "bag", stationId: s.id, label: s.name, mass: load?.mass ?? 0, arm: s.arm, latArm: s.latArm, maxMass: s.maxMass })
    })

    rows.push({ kind: "zfw", label: "Zero Fuel (ZFW)", mass: calc.emptyFuel.mass, arm: calc.emptyFuel.arm, latArm: calc.emptyFuel.latArm, moment: calc.emptyFuel.moment, latMoment: calc.emptyFuel.latMoment })

    fuelOnBoard.forEach(f => {
        rows.push({ kind: "fuel", stationId: f.station.id, label: f.station.name, mass: f.mass, arm: f.station.arm, latArm: f.station.latArm })
    })

    rows.push({ kind: "tow", label: "Take-off (TOW)", mass: calc.takeoff.mass, arm: calc.takeoff.arm, latArm: calc.takeoff.latArm, moment: calc.takeoff.moment, latMoment: calc.takeoff.latMoment })
    //with no fuel aboard nothing can burn — a "Trip burn" line there would not add up to LDGW
    const burnt = Math.round(calc.takeoff.mass - calc.landing.mass)
    if (tripFuelMass > 0 && burnt > 0) {
        rows.push({ kind: "trip", label: "Trip burn", mass: -burnt })
    }
    rows.push({ kind: "ldg", label: "Landing (LDGW)", mass: calc.landing.mass, arm: calc.landing.arm, latArm: calc.landing.latArm, moment: calc.landing.moment, latMoment: calc.landing.latMoment })
    return rows.map(withMoments)
}

export function buildWbLimitRows(input: WbLoadsheetInput): WbLimitRow[] {
    const { flight, calc } = input
    const aircraft = flight.aircraft
    const wb = aircraft.wb!
    const unit = aircraft.massUnit

    const towOut = checkPointOutOfEnvelope(aircraft, calc.takeoff)
    const ldgOut = checkPointOutOfEnvelope(aircraft, calc.landing)

    const rows: WbLimitRow[] = []

    const towOverMax = calc.takeoff.mass > getWbMaxMass(wb)
    rows.push({
        type: "limit", label: "Max TOW (" + unit + ")",
        value: fmtMass(calc.takeoff.mass),
        note: "/" + fmtMass(getWbMaxMass(wb)),
        ok: !towOverMax && !towOut.mass,
    })

    const landingFuelMass = calc.landing.landingFuelOnBoard.reduce((acc, f) => acc + f.mass, 0)
    const minLandingFuelMass = FuelConverter.toMassUnit(aircraft, aircraft.fuelPerHour / 3) //20 mins, same rule as calculateOutOfLimitsTexts
    rows.push({
        type: "limit", label: "Ldg fuel (" + unit + ")", value: fmtMass(landingFuelMass),
        note: "min " + fmtMass(minLandingFuelMass),
        ok: landingFuelMass >= minLandingFuelMass,
    })

    rows.push({ type: "section", label: "Longitudinal CG (mm)" })
    rows.push({ type: "limit", label: "Take-off", value: String(calc.takeoff.arm), note: towOut.arm ? "out of env" : "in env", ok: !towOut.arm })
    rows.push({ type: "limit", label: "Landing", value: String(calc.landing.arm), note: ldgOut.arm ? "out of env" : "in env", ok: !ldgOut.arm })

    if (hasLatEnvelope(aircraft)) {
        rows.push({ type: "section", label: "Lateral CG (mm)" })
        rows.push({ type: "limit", label: "Take-off", value: String(calc.takeoff.latArm), note: towOut.latArm ? "out of lims" : "in lims", ok: !towOut.latArm })
        rows.push({ type: "limit", label: "Landing", value: String(calc.landing.latArm), note: ldgOut.latArm ? "out of lims" : "in lims", ok: !ldgOut.latArm })
    }

    return rows
}

export function fmtMass(v: number): string {
    return Math.round(v).toLocaleString("en-US")
}

//moment in kg·mm → kg·m for display
export function fmtMoment(momentKgMm: number): string {
    return (momentKgMm / 1000).toFixed(1)
}

// ─── vector CG envelope (same SVG on screen and in the PDF) ─────────────────
//
// SECURITY NOTE: this string is inlined on screen via dangerouslySetInnerHTML.
// That is safe ONLY because every interpolated value is numeric (envelope points,
// computed masses/arms, tick values) or a closed union ("kg"|"lb"); all labels are
// hardcoded literals. Never interpolate user-entered text (names, remarks) here —
// if that is ever needed, sanitize it first.

export function renderWbEnvelopeSvg(opts: {
    aircraft: Aircraft
    calc: CalculatedFlightWbPoints
    mode: "lon" | "lat"
    isLatArmInWbPointUp: boolean
    vbW: number
    vbH: number
}): string {
    const { aircraft, calc, mode, isLatArmInWbPointUp, vbW: W, vbH: H } = opts
    const wb = aircraft.wb!
    const lat = mode === "lat"
    const m = { l: 42, r: 10, t: 9, b: 26 }

    //axis mapping. Lateral supports both model conventions (AircraftWbLatPoint union):
    // {mass, latArm}  → x=latArm, y=mass (typical helicopter chart, vertical centreline)
    // {arm, latArm}   → x=arm, y=latArm — or, when org.isLatArmInWbPointUp, x=latArm with arm
    //                   running DOWN the page (FWD at the top, like the cabin map and the old graph)
    type P = { arm: number; mass: number; latArm: number }
    var polyPts: { x: number; y: number }[]
    var toX: (p: P) => number, toY: (p: P) => number
    var xLab: string, yLab: string
    var centreline: "none" | "vertical" | "horizontal" = "none"
    var invertY = false

    if (!lat) {
        polyPts = wb.envelopePoints.map(p => ({ x: p.arm, y: p.mass }))
        toX = p => p.arm; toY = p => p.mass
        xLab = "arm (mm)"; yLab = "mass (" + aircraft.massUnit + ")"
    } else {
        const latPts = wb.latEnvelopePoints!
        const massBasis = "mass" in latPts[0]
        if (massBasis) {
            polyPts = latPts.map(p => ({ x: p.latArm, y: (p as { mass: number }).mass }))
            toX = p => p.latArm; toY = p => p.mass
            xLab = "lateral arm (mm)"; yLab = "mass (" + aircraft.massUnit + ")"
            centreline = "vertical"
        } else if (isLatArmInWbPointUp) {
            polyPts = latPts.map(p => ({ x: p.latArm, y: (p as { arm: number }).arm }))
            toX = p => p.latArm; toY = p => p.arm
            xLab = "lateral arm (mm)"; yLab = "arm (mm)"
            centreline = "vertical"
            invertY = true
        } else {
            polyPts = latPts.map(p => ({ x: (p as { arm: number }).arm, y: p.latArm }))
            toX = p => p.arm; toY = p => p.latArm
            xLab = "arm (mm)"; yLab = "lat arm (mm)"
            centreline = "horizontal"
        }
    }

    //the axes cover the envelope AND every plotted point — an exceedance must never be drawn off
    //the chart (the worst loadings were the invisible ones)
    const plotted = [calc.emptyFuel, calc.landing, calc.takeoff].map(p => ({ x: toX(p), y: toY(p) }))
    const allPts = polyPts.concat(plotted)
    var xMin = Math.min(...allPts.map(p => p.x)), xMax = Math.max(...allPts.map(p => p.x))
    var yMin = Math.min(...allPts.map(p => p.y)), yMax = Math.max(...allPts.map(p => p.y))
    const dx = (xMax - xMin) * 0.12 || 1, dy = (yMax - yMin) * 0.10 || 1
    xMin -= dx; xMax += dx; yMin -= dy; yMax += dy

    const px = (v: number) => m.l + (v - xMin) / (xMax - xMin) * (W - m.l - m.r)
    const py = (v: number) => invertY
        ? m.t + (v - yMin) / (yMax - yMin) * (H - m.t - m.b)
        : H - m.b - (v - yMin) / (yMax - yMin) * (H - m.t - m.b)

    const niceTicks = (lo: number, hi: number, n: number): number[] => {
        const step = Math.pow(10, Math.floor(Math.log10((hi - lo) / n)))
        const s = [1, 2, 2.5, 5, 10].map(x => x * step).find(x => (hi - lo) / x <= n) || step * 10
        const out: number[] = []
        for (var v = Math.ceil(lo / s) * s; v <= hi; v += s) out.push(+v.toFixed(6))
        return out
    }

    var s = `<rect x="${m.l}" y="${m.t}" width="${W - m.l - m.r}" height="${H - m.t - m.b}" fill="#ffffff" stroke="${LINE}"/>`
    niceTicks(xMin, xMax, 5).forEach(v => {
        s += `<line x1="${px(v)}" y1="${m.t}" x2="${px(v)}" y2="${H - m.b}" stroke="${LINE}"/>`
            + `<text x="${px(v)}" y="${H - m.b + 11}" font-size="8" fill="${MUT}" text-anchor="middle" font-family="Roboto">${v}</text>`
    })
    niceTicks(yMin, yMax, 4).forEach(v => {
        s += `<line x1="${m.l}" y1="${py(v)}" x2="${W - m.r}" y2="${py(v)}" stroke="${LINE}"/>`
            + `<text x="${m.l - 4}" y="${py(v) + 3}" font-size="8" fill="${MUT}" text-anchor="end" font-family="Roboto">${v}</text>`
    })

    if (centreline === "vertical" && xMin < 0 && xMax > 0) {
        s += `<line x1="${px(0)}" y1="${m.t + 12}" x2="${px(0)}" y2="${H - m.b}" stroke="${MUT}" stroke-dasharray="4 3"/>`
            + `<text x="${px(0)}" y="${m.t + 9}" font-size="7.5" fill="${MUT}" text-anchor="middle" font-family="Roboto">CL</text>`
            + `<text x="${px(0) - 5}" y="${H - m.b - 4}" font-size="7.5" fill="${MUT}" text-anchor="end" font-family="Roboto">LH</text>`
            + `<text x="${px(0) + 5}" y="${H - m.b - 4}" font-size="7.5" fill="${MUT}" text-anchor="start" font-family="Roboto">RH</text>`
    }
    if (centreline === "horizontal" && yMin < 0 && yMax > 0) {
        s += `<line x1="${m.l}" y1="${py(0)}" x2="${W - m.r}" y2="${py(0)}" stroke="${MUT}" stroke-dasharray="4 3"/>`
            + `<text x="${W - m.r - 3}" y="${py(0) - 3}" font-size="7.5" fill="${MUT}" text-anchor="end" font-family="Roboto">CL</text>`
    }
    if (invertY) {
        s += `<text x="${W - m.r - 3}" y="${m.t + 9}" font-size="7.5" fill="${MUT}" text-anchor="end" font-family="Roboto">FWD</text>`
    }

    s += `<text x="10" y="${H / 2}" font-size="7.5" fill="${MUT}" font-family="Roboto" transform="rotate(-90 10 ${H / 2})" text-anchor="middle">${yLab}</text>`
        + `<text x="${W - m.r}" y="${H - 3}" font-size="7.5" fill="${MUT}" text-anchor="end" font-family="Roboto">${xLab}</text>`
        + `<polygon points="${polyPts.map(p => px(p.x) + "," + py(p.y)).join(" ")}" fill="rgba(75,85,99,.05)" stroke="${ENV}" stroke-width="1.6"/>`

    const isOut = (p: P) => {
        const out = checkPointOutOfEnvelope(aircraft, p)
        return lat ? out.latArm : (out.arm || out.mass)
    }

    s += `<line x1="${px(toX(calc.takeoff))}" y1="${py(toY(calc.takeoff))}" x2="${px(toX(calc.landing))}" y2="${py(toY(calc.landing))}" stroke="${TRAVEL}" stroke-width="1.3" stroke-dasharray="3 2"/>`

    //markers first, labels after: labels are spread apart vertically so points sitting on (or near)
    //the same spot stay readable instead of overprinting into one blob
    const marks: { p: P, shape: "diamond" | "hollow" | "circle", color: string, label: string }[] = [
        { p: calc.emptyFuel, shape: "hollow", color: ZFW_C, label: "ZFW" },
        { p: calc.landing, shape: "circle", color: LDG_C, label: "LDG" },
        { p: calc.takeoff, shape: "diamond", color: TOW_C, label: "TOW" },
    ]
    const placed = marks.map(mk => ({ ...mk, x: px(toX(mk.p)), y: py(toY(mk.p)), k: isOut(mk.p) ? BAD : mk.color }))
    placed.forEach(({ x, y, k, shape }) => {
        s += shape === "diamond" ? `<polygon points="${x},${y - 6} ${x + 6},${y} ${x},${y + 6} ${x - 6},${y}" fill="${k}"/>`
            : shape === "hollow" ? `<polygon points="${x},${y - 6} ${x + 6},${y} ${x},${y + 6} ${x - 6},${y}" fill="#ffffff" stroke="${k}" stroke-width="1.6"/>`
            : `<circle cx="${x}" cy="${y}" r="5" fill="${k}"/>`
    })
    const LABEL_H = 9
    const labels = placed.map(pl => ({ ...pl, ly: pl.y + 3 })).sort((a, b) => a.ly - b.ly)
    for (var i = 1; i < labels.length; i++) {
        const prev = labels[i - 1], cur = labels[i]
        if (Math.abs(cur.x - prev.x) < 34 && cur.ly - prev.ly < LABEL_H) cur.ly = prev.ly + LABEL_H
    }
    //keep the spread stack inside the plot area
    const overflow = labels.length ? labels[labels.length - 1].ly - (H - m.b - 2) : 0
    if (overflow > 0) labels.forEach(l => { l.ly -= overflow })
    labels.forEach(({ x, ly, k, label }) => {
        const right = x + 8 + 22 > W - m.r
        s += `<text x="${right ? x - 8 : x + 8}" y="${ly}" font-size="8" font-weight="bold" fill="${k}" font-family="Roboto" text-anchor="${right ? "end" : "start"}">${label}</text>`
    })

    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}">${s}</svg>`
}

// ─── the A4 PDF (vector, one page per leg, merged into the briefing pack) ────

// geometry shared by the layout and the measurement pass
const PAGE_H = 841.89, X = 38, CONTENT_W = 519, GAP = 10, TOP = 34

// PIC SIGNATURE RESERVATION — pilotAccept (flightBriefingRouter.ts) stamps every W&B page at
// FIXED pdf-lib coords: text x=415/y=100(bottom), image x=355/y=65..105. That ink footprint is
// x 355–575, y-bottom 35–115 → top-based y 727–807. The frame is drawn on EVERY page (background)
// and the bottom page margin keeps all flowing content (table rows included) above it.
const SIG = { x: 345, yTop: 724, w: 232, h: 84 }
const FLOW_BOTTOM = SIG.yTop - 12             //nothing in the flow may go below this line
const BOT = PAGE_H - FLOW_BOTTOM                //bottom page margin that enforces it
const CREDITS_Y = SIG.yTop + SIG.h - 24         //prepared-by + page stamp, baseline-aligned with the box bottom
const MIN_CHART_H = 150

export type WbLayoutMeasure = {
    //per sheet: page and top y of the leg's first node, and where its loading table ended
    [sheetIndex: number]: { startPage: number, tablePage: number, tableBottom: number }
}

export function buildWbLoadsheetDocDefinition(inputOrInputs: WbLoadsheetInput | WbLoadsheetInput[], measure?: WbLayoutMeasure | "probe"): TDocumentDefinitions {
    const inputs = Array.isArray(inputOrInputs) ? inputOrInputs : [inputOrInputs]
    const first = inputs[0]
    const flight = first.flight
    const content: Content[] = []
    inputs.forEach((input, i) => {
        content.push(...buildSheetContent(input, i, measure))
    })

    const title = "W&B Loadsheet " + flight.flightId + (first.revision ? " (Revision " + first.revision.n + ")" : "")
    return {
        pageSize: "A4",
        pageMargins: [X, TOP, X, BOT],
        defaultStyle: { fontSize: 8.5, color: INK },
        info: { title },
        //PIC signature frame on every page — the server's pilotAccept ink lands inside this box
        background: () => [
            { canvas: [{ type: "rect", x: SIG.x, y: SIG.yTop, w: SIG.w, h: SIG.h, r: 6, lineWidth: 0.9, lineColor: LINE }] },
            //label sits on the box's bottom edge — the server ink occupies the upper area
            { absolutePosition: { x: SIG.x + 9, y: SIG.yTop + SIG.h - 12 },
              text: "PIC — SIGNED ON BRIEFING ACCEPTANCE", fontSize: 6, color: FAINT, characterSpacing: 0.6 },
        ],
        //bottom-left credits, baseline-aligned with the signature box bottom
        footer: (currentPage: number, pageCount: number) => ({
            margin: [X, CREDITS_Y - FLOW_BOTTOM, 0, 0],
            columns: [{ width: SIG.x - X - 12, stack: [
                { text: "Prepared by - " + getCrewFullName(flight.pic), fontSize: 8, color: MUT },
                { text: "AviTracer BRIEF · W&B loadsheet" + (first.revision ? " · Revision " + first.revision.n : "") + " · Page " + currentPage + " / " + pageCount, fontSize: 7, color: FAINT, margin: [0, 4, 0, 0] },
            ]}],
        }),
        content,
        styles: {
            th: { fontSize: 7.5, color: MUT, bold: true }, thR: { fontSize: 7.5, color: MUT, bold: true, alignment: "right" },
            td: { fontSize: 8.5 }, tdR: { fontSize: 8.5, alignment: "right" },
            sub: { fontSize: 8.5, color: MUT }, subR: { fontSize: 8.5, color: MUT, alignment: "right" },
            totZ: { fontSize: 9, bold: true }, totZR: { fontSize: 9, bold: true, alignment: "right" },
            totT: { fontSize: 9, bold: true }, totTR: { fontSize: 9, bold: true, alignment: "right" },
            totL: { fontSize: 9, bold: true }, totLR: { fontSize: 9, bold: true, alignment: "right" },
            lh: { fontSize: 7, color: "#ffffff", bold: true, characterSpacing: 1 },
            sec: { fontSize: 6.5, color: INK, bold: true, characterSpacing: 0.8 },
            lsec: { fontSize: 6.5, color: INK, bold: true, characterSpacing: 0.8 },
            lk: { fontSize: 8, color: INK2 }, lv: { fontSize: 8, bold: true, alignment: "right" },
            ld: { fontSize: 7.5, color: OK, alignment: "right" }, ldBad: { fontSize: 7.5, color: BAD, alignment: "right" },
            lt: { fontSize: 8, color: OK, alignment: "center" }, ltBad: { fontSize: 8, color: BAD, alignment: "center" },
            cap: { fontSize: 7.5, bold: true, color: INK2 },
        },
    }
}

function buildSheetContent(input: WbLoadsheetInput, sheetIndex: number, measure?: WbLayoutMeasure | "probe"): Content[] {
    const { flight, calc, errors, warnings, leg, revision } = input
    const aircraft = flight.aircraft
    const wb = aircraft.wb!
    const unit = aircraft.massUnit
    const L = hasLatEnvelope(aircraft)
    const ok = errors.length === 0

    // loading table — all AviTracer W&B profiles are authored in kg and mm (db-wide
    // convention), so mass×arm/1000 prints as honest kg·m
    const head: TableCell[] = [
        { text: "STATION", style: "th" }, { text: "WEIGHT (" + unit + ")", style: "thR" },
        { text: "ARM (mm)", style: "thR" }, { text: "MOM (kg·m)", style: "thR" },
    ]
    if (L) head.push({ text: "LAT ARM (mm)", style: "thR" }, { text: "LAT MOM", style: "thR" })

    const totalKinds = ["zfw", "tow", "ldg"]
    const body: TableCell[][] = [head]
    const colCount = L ? 6 : 4
    const pushSection = (label: string) => {
        const cells: TableCell[] = [{ text: label, style: "sec", colSpan: colCount }]
        for (var i = 1; i < colCount; i++) cells.push({})
        body.push(cells)
    }
    var lastKind: WbSheetRow["kind"] | undefined
    //ALL positions print — empty seats and empty bays included (muted), matching the list view
    buildWbSheetRows(input)
        .filter(r => (r.kind === "fuel" ? r.mass > 0 : true))
        .forEach(r => {
            //separate persons from cargo, matching the on-screen table
            if (r.kind === "seat" && lastKind !== "seat") pushSection("CREW & PASSENGERS")
            if (r.kind === "bag" && lastKind !== "bag") pushSection("BAGGAGE & CARGO")
            lastKind = r.kind
            const isTotal = totalKinds.includes(r.kind)
            const isEmptyPosition = (r.kind === "seat" && r.mass <= 0 && (r.name ?? "") === "") || (r.kind === "bag" && r.mass <= 0)
            //per-phase total styles (totZ/totT/totL) drive the tinted row fills in the layout
            const style = isTotal ? ("tot" + (r.kind === "zfw" ? "Z" : r.kind === "tow" ? "T" : "L"))
                : (r.kind === "bew" || r.kind === "trip" || r.kind === "fuel" || isEmptyPosition ? "sub" : "td")
            //NB: no ◆◇ text glyphs here — pdfmake's bundled Roboto subset lacks them (renders
            //tofu; verified). The tinted rows carry phase identity in the table; the chart's
            //shape-coded markers are SVG paths, so greyscale safety is preserved there.
            //Seat rows mirror the on-screen hierarchy: STATION as a small muted caps label,
            //the occupant's name bold (cargo on a seat in italics, not like a person).
            const load = r.kind === "seat" ? input.wbLoads.find(l => l.station.id === r.stationId) : undefined
            const isCargo = load?.loadType === "other"
            const label: string | { text: string; fontSize?: number; color?: string; bold?: boolean; italics?: boolean }[] = r.kind === "seat"
                ? [
                    { text: r.label.toUpperCase() + "  ", fontSize: 6.5, color: MUT, bold: true },
                    r.name ? { text: r.name, bold: !isCargo, italics: isCargo, color: INK } : { text: "—", color: FAINT },
                  ]
                : r.label
            const loadedPosition = (r.kind === "seat" || r.kind === "bag") && r.mass > 0
            const cells: TableCell[] = [
                { text: label, style },
                { text: fmtMass(r.mass), style: style + "R", bold: loadedPosition || undefined },
                { text: r.arm !== undefined ? String(r.arm) : "", style: style + "R" },
                { text: r.moment !== undefined ? fmtMoment(r.moment) : "", style: style + "R" },
            ]
            if (L) cells.push(
                { text: r.arm !== undefined ? String(r.latArm ?? 0) : "", style: style + "R" },
                { text: r.latMoment !== undefined ? fmtMoment(r.latMoment) : "", style: style + "R" },
            )
            body.push(cells)
        })

    // limits box
    const limBody: TableCell[][] = [[{ text: "LIMITS & MARGINS", style: "lh", colSpan: 4 }, {}, {}, {}]]
    buildWbLimitRows(input).forEach(r => {
        if (r.type === "section") {
            limBody.push([{ text: r.label.toUpperCase(), style: "lsec", colSpan: 4 }, {}, {}, {}])
        } else {
            limBody.push([
                { text: r.label, style: "lk" }, { text: r.value, style: "lv" },
                { text: r.note, style: r.ok ? "ld" : "ldBad" }, { text: r.ok ? "OK" : "X", style: r.ok ? "lt" : "ltBad" },
            ])
        }
    })

    // charts
    const LIM_W = 184 //fits "1,646/1,715" + a "+NN" overage note on one line in the Max TOW row
    const colW = (CONTENT_W - GAP) / 2
    const cardW = L ? colW : (CONTENT_W - LIM_W - GAP) //single chart aligns with the table column
    const svgW = cardW - 8
    const vbW = 330
    const MAX_CARD = Math.round(svgW * (L ? 0.9 : 0.62)) + 22

    //card chrome: title row + table paddings/borders + the svg's bottom margin. The svg gets the
    //rest, so the card's REAL height stays within cardH (a card taller than its slot would spill
    //onto a new page).
    const CARD_CHROME = 30
    const chartCard = (title: string, mode: "lon" | "lat", cardH: number): Column => {
        const vbH = Math.round(vbW * (cardH - CARD_CHROME) / svgW)
        return {
            width: cardW,
            table: { widths: ["*"], body: [
                [{ text: title, style: "cap", margin: [7, 4, 7, 1] }],
                [{ svg: renderWbEnvelopeSvg({ aircraft, calc, mode, isLatArmInWbPointUp: input.isLatArmInWbPointUp, vbW, vbH }), width: svgW, alignment: "center", margin: [3, 0, 3, 3] }],
            ]},
            layout: { hLineColor: () => LINE, vLineColor: () => LINE, hLineWidth: () => 0.8, vLineWidth: () => 0.8 },
        }
    }
    const chartColumns = (cardH: number): Column[] => L
        ? [chartCard("CG ENVELOPE - LONGITUDINAL", "lon", cardH), chartCard("CG ENVELOPE - LATERAL", "lat", cardH)]
        : [chartCard("CG ENVELOPE - LONGITUDINAL", "lon", cardH)]

    //Charts are pinned to the bottom of the leg's first page when the MEASURED table end leaves
    //room (no dead space beneath them; slack lands between table and charts). When it doesn't, or
    //when no measurement is available, they flow after the table and pdfmake moves them to a new
    //page as one unbreakable block — they can never overlap the table.
    var charts: Content | undefined
    const m = measure !== undefined && measure !== "probe" ? measure[sheetIndex] : undefined
    if (measure === "probe") {
        charts = undefined
    } else if (m && m.tablePage === m.startPage && FLOW_BOTTOM - (m.tableBottom + 14) >= MIN_CHART_H) {
        const cardH = Math.min(FLOW_BOTTOM - (m.tableBottom + 14), MAX_CARD)
        charts = { absolutePosition: { x: X, y: FLOW_BOTTOM - cardH - 2 }, columnGap: GAP, columns: chartColumns(cardH) }
    } else {
        charts = { unbreakable: true, margin: [0, 14, 0, 0], columnGap: GAP, columns: chartColumns(Math.min(MAX_CARD, FLOW_BOTTOM - TOP - 20)) }
    }

    const statusSub = ok
        ? "TOW " + fmtMass(calc.takeoff.mass) + "/" + fmtMass(getWbMaxMass(wb)) + " " + unit
            + " · CG " + calc.takeoff.arm + " mm"
            + (L ? " · lat " + calc.takeoff.latArm + " mm" : "") + " · within envelope"
            + (warnings.length > 0 ? "  |  " + warnings.join(" · ") : "")
        : errors.join(" · ")

    const out: Content[] = [
        // identity band
        { id: "wb-sheet-start-" + sheetIndex, pageBreak: sheetIndex > 0 ? "before" : undefined, columns: [
            { width: "*", stack: [
                { text: revision ? "REVISED WEIGHT & BALANCE LOADSHEET" : "WEIGHT & BALANCE LOADSHEET", bold: true, fontSize: 13, color: revision ? REV : INK },
                { text: [
                    { text: "Reg ", color: MUT }, { text: aircraft.registration + "   ", bold: true },
                    { text: "Type ", color: MUT }, { text: aircraft.icaoType, bold: true },
                ], fontSize: 9, margin: [0, 3, 0, 0] },
                { text: [
                    { text: "Flight ", color: MUT }, { text: flight.flightId + "   ", bold: true },
                    { text: "Route ", color: MUT }, { text: wbRouteTitle(flight, leg) + "   ", bold: true },
                    { text: "Date ", color: MUT }, { text: moment(flight.departureDateTime).format("DD MMM YYYY") + "   ", bold: true },
                    { text: "PIC ", color: MUT }, { text: getCrewFullName(flight.pic), bold: true },
                ], fontSize: 9, margin: [0, 1, 0, 0] },
                ...(leg ? [{ text: [
                    { text: "Leg ", color: MUT }, { text: (leg.index + 1) + " of " + leg.count + "   ", bold: true },
                    { text: "Leg time ", color: MUT }, { text: leg.eetMins + " min", bold: true },
                ], fontSize: 9, margin: [0, 1, 0, 0] } as Content] : []),
                { text: "Weights in " + unit + " · Arms in mm · Moment = Weight × Arm / 1000 (kg·m)", fontSize: 7.5, color: MUT, margin: [0, 3, 0, 0] },
            ]},
            input.logoSvg
                ? { svg: input.logoSvg, width: 100 } //brief_logo.svg (300×130) → 100×43pt, top right
                : { width: "auto", stack: [
                    { text: "AviTracer", color: INK, bold: true, fontSize: 11, alignment: "right" }, //logo is black — mono brand
                    { text: "BRIEF", color: MUT, fontSize: 9, alignment: "right", characterSpacing: 2 },
                ]},
        ]} as Content, //pdfmake's typings omit `id` on columns; the layout engine reads it from any node
        { canvas: [{ type: "line", x1: 0, y1: 2, x2: CONTENT_W, y2: 2, lineWidth: 1.4, lineColor: INK }], margin: [0, 4, 0, 8] },
    ]

    if (revision) {
        out.push({ table: { widths: ["*"], body: [[
            { stack: [
                { text: "REVISION " + revision.n + " — last-minute change. Supersedes the W&B page(s) of the briefing pack.", bold: true, color: REV, fontSize: 8.5 },
                //pdfmake's bundled Roboto has no arrow glyph (prints tofu) — the screen keeps "→"
                ...revision.summary.map(line => ({ text: "• " + line.replace(/→/g, "->"), color: INK2, fontSize: 8, margin: [0, 2, 0, 0] } as Content)),
                ...(revision.fuelChanged ? [{ text: "Fuel differs from planned: the navlog and the filed FPL still show the planned fuel.", color: REV, fontSize: 7.5, italics: true, margin: [0, 3, 0, 0] } as Content] : []),
            ], margin: [8, 5, 8, 5] },
        ]]},
        layout: { fillColor: () => REVBG, hLineColor: () => REV, vLineColor: () => REV, hLineWidth: () => 1, vLineWidth: () => 1 },
        margin: [0, 0, 0, 7] })
    }

    out.push(
        // status band
        { table: { widths: ["*"], body: [[
            { text: [
                { text: ok ? "WITHIN LIMITS" : errors.length + " LIMIT(S) EXCEEDED", bold: true, color: ok ? OK : BAD },
                { text: "    " + statusSub, color: ok ? OK : BAD, fontSize: 8 },
            ], margin: [8, 5, 8, 5] },
        ]]},
        layout: { fillColor: () => ok ? OKBG : BADBG, hLineColor: () => ok ? OK : BAD, vLineColor: () => ok ? OK : BAD, hLineWidth: () => 1, vLineWidth: () => 1 },
        margin: [0, 0, 0, 9] },

        // loading table | limits box
        { columns: [
            { width: "*", table: { headerRows: 1, widths: L ? ["*", "auto", "auto", "auto", "auto", "auto"] : ["*", "auto", "auto", "auto"], body },
              layout: {
                  hLineWidth: (i: number, node: any) => (i === 0 || i === node.table.body.length) ? 0 : (isTotalRow(node, i) ? 1.3 : 0.6),
                  hLineColor: (i: number, node: any) => isTotalRow(node, i) ? INK : LINE,
                  fillColor: (i: number, node: any) => isSecRow(node, i) ? SEC_BG : phaseFill(node, i),
                  vLineWidth: () => 0, paddingTop: () => 3, paddingBottom: () => 3,
              } },
            { width: LIM_W, table: { widths: ["*", "auto", "auto", 17], body: limBody },
              layout: {
                  fillColor: (i: number, node: any) => i === 0 ? INK : (isSectionRow(node, i) ? SEC_BG : null),
                  hLineColor: () => LINE, vLineColor: () => "#ffffff",
                  hLineWidth: () => 0.6, vLineWidth: () => 0,
                  paddingTop: () => 2.5, paddingBottom: () => 2.5, paddingLeft: () => 7,
              } },
        ], columnGap: GAP },

        //measurement marker: where the loading block ended (read back by measureWbLayout)
        { id: "wb-after-table-" + sheetIndex, text: " ", fontSize: 1 },
    )
    if (charts) out.push(charts)
    return out

    function isTotalRow(node: any, i: number): boolean {
        const r = node.table.body[i]
        return r && r[0] && typeof r[0].style === "string" && r[0].style.startsWith("tot")
    }
    function phaseFill(node: any, i: number): string | null {
        const r = node.table.body[i]
        const st = r && r[0] && r[0].style
        return st === "totZ" ? ZFW_BG : st === "totT" ? TOW_BG : st === "totL" ? LDG_BG : null
    }
    function isSecRow(node: any, i: number): boolean {
        const r = node.table.body[i]
        return r && r[0] && r[0].style === "sec"
    }
    function isSectionRow(node: any, i: number): boolean {
        const r = node.table.body[i]
        return r && r[0] && r[0].style === "lsec"
    }
}

//pdfmake 0.3.x output methods RETURN A PROMISE and ignore a callback argument (verified — a
//callback-only wrapper hangs forever); 0.2.x was callback-based. Support both shapes.
function pdfOutput<T>(created: any, method: "getBlob" | "getBuffer"): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        try {
            const result = created[method]((out: T) => resolve(out)) //0.2 path
            if (result && typeof result.then === "function") {
                result.then(resolve, reject) //0.3 path
            }
        } catch (e) {
            reject(e)
        }
    })
}

//Lays the sheets out once WITHOUT charts and reads back, through pdfmake's pageBreakBefore hook
//(called with every node's real position), where each leg starts and where its table ended.
export async function measureWbLayout(pdfMake: any, inputs: WbLoadsheetInput[]): Promise<WbLayoutMeasure> {
    const measure: WbLayoutMeasure = {}
    const starts: { [i: number]: number } = {}
    const probe = buildWbLoadsheetDocDefinition(inputs, "probe")
    probe.pageBreakBefore = (node: any) => {
        const id: string | undefined = node.id
        if (id?.startsWith("wb-sheet-start-")) {
            starts[Number(id.slice("wb-sheet-start-".length))] = node.startPosition.pageNumber
        } else if (id?.startsWith("wb-after-table-")) {
            const i = Number(id.slice("wb-after-table-".length))
            measure[i] = { startPage: starts[i] ?? node.startPosition.pageNumber, tablePage: node.startPosition.pageNumber, tableBottom: node.startPosition.top }
        }
        return false
    }
    await pdfOutput<unknown>(pdfMake.createPdf(probe), "getBuffer")
    return measure
}

export async function loadPdfMake(): Promise<any> {
    //pdfmake is only needed on save — load it lazily to keep it out of the main bundle.
    const pdfMakeModule: any = await import("pdfmake/build/pdfmake")
    const pdfFontsModule: any = await import("pdfmake/build/vfs_fonts")
    const pdfMake = pdfMakeModule.default ?? pdfMakeModule

    //pdfmake 0.3.x: vfs_fonts exports the vfs object directly; 0.2.x nested it under pdfMake.vfs
    const fontsModule = pdfFontsModule.default ?? pdfFontsModule
    const vfs = fontsModule.pdfMake?.vfs ?? fontsModule.vfs ?? fontsModule
    if (typeof pdfMake.addVirtualFileSystem === "function") {
        pdfMake.addVirtualFileSystem(vfs)
    } else {
        pdfMake.vfs = vfs
    }
    return pdfMake
}

//One document with a sheet per leg (a single sheet for a flight without stops).
export async function createWbPdfBlob(inputOrInputs: WbLoadsheetInput | WbLoadsheetInput[]): Promise<Blob> {
    const pdfMake = await loadPdfMake()
    const inputs = Array.isArray(inputOrInputs) ? inputOrInputs : [inputOrInputs]

    //company logo for the header — same asset the app serves; fall back to the text wordmark
    var logoSvg: string | undefined = inputs[0].logoSvg
    if (!logoSvg) {
        try {
            const resp = await fetch("/assets/brief/brief_logo.svg")
            if (resp.ok) logoSvg = await resp.text()
        } catch (e) {
            //offline/asset missing → text wordmark
        }
    }
    const withLogo = inputs.map(input => ({ ...input, logoSvg }))

    const measure = await measureWbLayout(pdfMake, withLogo)
    return pdfOutput<Blob>(pdfMake.createPdf(buildWbLoadsheetDocDefinition(withLogo, measure)), "getBlob")
}
